/**
 * Print-only chapter shell atom for Monthly Consultation PDF.
 * Provides Canva-inspired page chrome, title, and CSS hooks for A4 page breaks.
 */

import React from "react";
import watermarkLuopan from "../../../../assets/monthly-consultation/pdf/watermark-luopan.png";
import { paddedPageLabel } from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  charcoal,
  displayFontFamily,
  mutedGray,
  pagePaddingX,
  pagePaddingY,
  terracotta,
} from "../pdfTheme";

/**
 * Props for one PDF chapter page shell.
 */
export interface PdfChapterShellProps {
  /** Chapter id used for `data-pdf-chapter` (e.g. "cover", "archetype"). */
  chapterId: string;
  /** Small-caps section label shown top-left. */
  eyebrow: string;
  /** Main chapter display title (Georgia). */
  title: string;
  /**
   * 1-based sequential page index among rendered sheets.
   * When provided, the footer right shows `PAGE 01` style (zero-padded).
   */
  pageNumber?: number;
  /**
   * Cover poster mode: hide eyebrow, title, default footer, and page inset
   * so the cover can draw full-bleed gradient / brand bars.
   */
  coverMode?: boolean;
  /**
   * Hide the default eyebrow + title row while keeping the footer.
   * Used by Overview, which supplies its own editorial header.
   */
  hideHeader?: boolean;
  /**
   * Hide the right-side chapter-id label (e.g. "ACTIVATIONS") while keeping
   * the terracotta eyebrow, display title, footer, and PAGE NN.
   * Defaults to false so other chapters are unchanged.
   */
  hideChapterLabel?: boolean;
  /**
   * Page break policy before this chapter.
   * - always: force a new A4 page (default for long chapters)
   * - auto: flow after the previous chapter
   */
  breakBefore?: "always" | "auto";
  /**
   * When true, stretch the chapter to at least one full A4 height (297mm)
   * and pin the footer to the sheet bottom. Defaults to true when
   * breakBefore is "always".
   */
  fillPage?: boolean;
  /** Tighter title / footer spacing for dense pack pages. */
  compact?: boolean;
  /** Chapter body content. */
  children: React.ReactNode;
}

/**
 * True when value is a finite 1-based page index.
 *
 * @param value - Optional page number from props
 * @returns Whether the value can be shown as `PAGE NN`
 */
function isValidPageNumber(value: number | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 1;
}

/**
 * Resolve the webpack URL for the inner-page luopan watermark.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function watermarkSrc(): string {
  return typeof watermarkLuopan === "string" ? watermarkLuopan.trim() : "";
}

/**
 * Wraps one PDF chapter with Canva page chrome and print CSS hooks.
 *
 * Applies `.mc-pdf-chapter` / `.mc-pdf-chapter--auto` and `.mc-pdf-page`
 * (cream + dot-grid canvas). Header shows terracotta eyebrow left and chapter
 * id label right (unless `hideChapterLabel`); footer shows product label +
 * `PAGE NN` (or chapter-id fallback). Cover mode uses `.mc-pdf-page--cover`
 * (no inset, no footer, no watermark).
 */
export const PdfChapterShell: React.FC<PdfChapterShellProps> = ({
  chapterId,
  eyebrow,
  title,
  pageNumber,
  coverMode = false,
  hideHeader = false,
  hideChapterLabel = false,
  breakBefore = "always",
  fillPage,
  compact = false,
  children,
}) => {
  const safeChapterId =
    typeof chapterId === "string" ? chapterId.trim() : "";
  const safeEyebrow = typeof eyebrow === "string" ? eyebrow.trim() : "";
  const safeTitle = typeof title === "string" ? title.trim() : "";
  const isCover = coverMode === true;
  const skipHeader = isCover || hideHeader === true;
  const skipChapterLabel = hideChapterLabel === true;
  const luopan = watermarkSrc();

  const chapterLabel =
    safeChapterId.length > 0 ? safeChapterId.replace(/-/g, " ").toUpperCase() : "";

  const footerRightLabel = isValidPageNumber(pageNumber)
    ? paddedPageLabel(pageNumber)
    : chapterLabel;

  const shouldFillPage =
    typeof fillPage === "boolean" ? fillPage : breakBefore === "always";

  const breakClass =
    breakBefore === "auto" ? "mc-pdf-chapter mc-pdf-chapter--auto" : "mc-pdf-chapter";

  const pageClass = [
    breakClass,
    "mc-pdf-page",
    isCover ? "mc-pdf-page--cover" : "",
  ]
    .filter((item) => item.length > 0)
    .join(" ");

  return (
    <section
      className={pageClass}
      data-pdf-chapter={safeChapterId.length > 0 ? safeChapterId : undefined}
      style={{
        boxSizing: "border-box",
        padding: isCover
          ? 0
          : compact
            ? `${pagePaddingY - 8}px ${pagePaddingX}px`
            : `${pagePaddingY}px ${pagePaddingX}px`,
        /*
         * Fill at least one A4 sheet when requested. Use min-height (not fixed
         * height) so dense chapters can still overflow onto a second sheet.
         * CSS also sets min-height:297mm on .mc-pdf-page for print + screen.
         */
        minHeight: shouldFillPage ? "297mm" : undefined,
        display: "flex",
        flexDirection: "column",
        color: charcoal,
        position: "relative",
      }}
    >
      {!isCover && luopan.length > 0 ? (
        <img
          src={luopan}
          alt=""
          aria-hidden="true"
          className="mc-pdf-watermark"
          style={{
            position: "absolute",
            left: "50%",
            top: "46%",
            transform: "translate(-50%, -50%)",
            width: 240,
            height: "auto",
            opacity: 0.045,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ) : null}

      {/* Top chrome: terracotta eyebrow left, chapter id right */}
      {skipHeader ? null : (
        <header
          className="mc-pdf-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 16,
            marginBottom: compact ? 8 : 12,
            fontFamily: bodyFontFamily,
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          <p
            className="mc-pdf-eyebrow"
            style={{
              margin: 0,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: terracotta,
            }}
          >
            {safeEyebrow}
          </p>
          {!skipChapterLabel && chapterLabel.length > 0 ? (
            <p
              className="mc-pdf-page-label"
              style={{
                margin: 0,
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: mutedGray,
                flexShrink: 0,
              }}
            >
              {chapterLabel}
            </p>
          ) : null}
        </header>
      )}

      {/* Chapter display title — omitted in coverMode and hideHeader */}
      {!skipHeader && safeTitle.length > 0 ? (
        <h2
          style={{
            margin: compact ? "0 0 12px" : "0 0 20px",
            fontFamily: displayFontFamily,
            fontSize: compact ? 22 : 26,
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            color: charcoal,
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          {safeTitle}
        </h2>
      ) : null}

      {/*
        Body grows into leftover A4 space so short chapters still fill the sheet.
        flex:1 1 auto (not a fixed height) — long chapters grow past 297mm and
        paginate naturally. Cover children must fill the poster for the brand bar.
      */}
      <div
        style={{
          minWidth: 0,
          flex: isCover || shouldFillPage ? "1 1 auto" : "0 1 auto",
          display: isCover || shouldFillPage ? "flex" : undefined,
          flexDirection: isCover || shouldFillPage ? "column" : undefined,
          position: "relative",
          zIndex: 1,
        }}
      >
        {children}
      </div>

      {/* Footer chrome — omitted in coverMode (cover draws its own brand bar) */}
      {isCover ? null : (
        <footer
          className="mc-pdf-footer"
          style={{
            /*
             * Pin footer to the bottom of the A4 sheet when content is short.
             * CSS also sets margin-top:auto on .mc-pdf-footer; keep a compact
             * gap only when this chapter deliberately does not fill the page.
             */
            marginTop: shouldFillPage ? "auto" : compact ? 12 : 16,
            paddingTop: 10,
            borderTop: `1px solid ${border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            fontFamily: bodyFontFamily,
            breakInside: "avoid",
            pageBreakInside: "avoid",
            breakBefore: "avoid",
            pageBreakBefore: "avoid",
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: mutedGray,
            }}
          >
            ZI WEI DOU SHU PERSONAL REPORT
          </span>
          {footerRightLabel.length > 0 ? (
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: mutedGray,
                flexShrink: 0,
              }}
            >
              {footerRightLabel}
            </span>
          ) : null}
        </footer>
      )}
    </section>
  );
};
